/**
 * Farbregeln für Zeilen und Deadline-Zellen (REQ-09 §4). Bewusst reine Funktionen,
 * damit sie ohne Rendern prüfbar sind.
 */

import type { Container, OrderPosition } from '@/types/entities';
import { parseApiDate } from '@/utils/date';

export type DeadlineStyle = '' | 'green' | 'darkred' | 'red' | 'yellow';

/**
 * Vergleiche zweier `Nullable<DateTime>` liefern in C# `false`, sobald eine Seite `null`
 * ist — auch bei `<=`. Diese Helfer bilden das nach, damit die Farblogik dieselben
 * Ergebnisse liefert wie der Altclient.
 */
export function isBefore(a: Date | null, b: Date | null): boolean {
  return a !== null && b !== null && a.getTime() < b.getTime();
}

export function isAfter(a: Date | null, b: Date | null): boolean {
  return a !== null && b !== null && a.getTime() > b.getTime();
}

export function isOnOrBefore(a: Date | null, b: Date | null): boolean {
  return a !== null && b !== null && a.getTime() <= b.getTime();
}

/** Pendant zu `DateTime.Date` — schneidet die Uhrzeit ab. */
function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/** Pendant zu `DateTime.Today.AddDays(days)`. */
function todayPlusDays(days: number): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
}

/**
 * Gemeinsame Regel von `IwmColumnsContainer.GetRowColorCssClass` und
 * `IwmColumnsBestellposition.GetRowColorCssClass` — beide unterscheiden sich nur in den
 * Feldnamen.
 */
function deliveryRowColor(
  anlieferdatumEmpf: string | null,
  fruehestAnlieferdatum: string | null,
  terminBestaetigung: boolean | null,
): string {
  const empf = parseApiDate(anlieferdatumEmpf);
  if (isBefore(empf, parseApiDate(fruehestAnlieferdatum))) return 'row-red';
  if (empf !== null && terminBestaetigung === false) return 'row-yellow';
  if (terminBestaetigung === true) return 'row-green';
  return '';
}

export function getContainerRowColor(model: Container): string {
  return deliveryRowColor(
    model.AnlieferdatumEmpf,
    model.FruehestAnlieferdatum,
    model.TerminBestaetigungCif,
  );
}

export function getOrderPositionRowColor(model: OrderPosition): string {
  return deliveryRowColor(
    model.Container_AnlieferdatumEmpf,
    model.Container_FruehestAnlieferdatum,
    model.Container_TerminBestaetigungCIF1,
  );
}

/**
 * Port von `GetKalkEKRowColorCssClass`. `Einzelpreis` und `Menge` sind im Domänenmodell
 * nicht nullable — deren `!= null`-Prüfung aus dem Altclient ist immer wahr und entfällt.
 * Die zweite Prüfung von `Waehrungskurs` im Original ist ebenfalls redundant.
 */
export function getKalkEkRowColor(model: OrderPosition): string {
  if (model.Incoterm === null || model.Waehrungskurs === null) return '';

  const hasAmounts = model.Einzelpreis > 0 && model.Menge > 0;
  if (model.Incoterm !== 'FOB' && hasAmounts && model.AnzahlRgSeefracht > 0) return 'row-green';
  if (model.Incoterm === 'FOB' && hasAmounts) return 'row-green';
  return '';
}

/**
 * Port von `IwmSharedColumnsDeadline.GetStyle`. Die Reihenfolge der Zweige ist bindend.
 * `Final < Deadline` vergleicht die vollen Zeitstempel, die beiden letzten Zweige
 * vergleichen auf Tagesebene (`Deadline.Date` gegen `DateTime.Today`).
 */
export function getDeadlineStyle(deadline: string | null, final: string | null): DeadlineStyle {
  const deadlineDate = parseApiDate(deadline);
  if (deadlineDate === null) return '';

  const finalDate = parseApiDate(final);
  if (isBefore(finalDate, deadlineDate)) return 'green';
  if (isAfter(finalDate, deadlineDate)) return 'darkred';
  if (finalDate !== null) return '';

  const deadlineDay = startOfDay(deadlineDate);
  if (isBefore(deadlineDay, todayPlusDays(0))) return 'red';
  if (isOnOrBefore(deadlineDay, todayPlusDays(14))) return 'yellow';
  return '';
}
